import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { Pencil, Trash2 } from 'lucide-react';
import { JOB_STATUSES, JOB_STAGES, WORK_SETUPS, JOB_SOURCES, STATUS_COLORS } from '../utils/constants';
import './JobModal.css';

const Field = ({ label, children }) => (
  <div className="modal-field">
    <label className="modal-field-label">{label}</label>
    {children}
  </div>
);

const initSalaryMode = (job) =>
  job.salaryMin && job.salaryMax && job.salaryMin === job.salaryMax ? 'exact' : 'range';

const fmtSalaryView = (job) => {
  if (!job.salaryMin && !job.salaryMax) return '—';
  const cur = job.salaryCurrency || 'PHP';
  const f = n => n?.toLocaleString();
  if (job.salaryMin && job.salaryMax) {
    if (job.salaryMin === job.salaryMax) return `${cur} ${f(job.salaryMin)}`;
    return `${cur} ${f(job.salaryMin)} – ${f(job.salaryMax)}`;
  }
  if (job.salaryMin) return `${cur} ${f(job.salaryMin)}+`;
  return `Up to ${cur} ${f(job.salaryMax)}`;
};

const JobModal = ({ job, onClose, onUpdate, onDelete }) => {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ ...job });
  const [salaryMode, setSalaryMode] = useState(() => initSalaryMode(job));
  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const handleSave = async () => {
    setSaving(true);
    await onUpdate(job._id, form);
    setSaving(false);
    setEditing(false);
  };

  const handleDelete = async () => {
    await onDelete(job._id);
  };

  const fmtDate = (d) => {
    if (!d) return '—';
    try { return format(new Date(d), 'MMM dd, yyyy'); }
    catch { return '—'; }
  };

  const statusColor = STATUS_COLORS[form.status] || {};

  return (
    <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal-panel">
        {/* Header */}
        <div className="modal-header">
          <div className="modal-title-group">
            <div className="modal-company-avatar">{form.company[0]?.toUpperCase()}</div>
            <div>
              <h2 className="modal-company">{form.company}</h2>
              <p className="modal-role">{form.role}</p>
            </div>
            <span
              className="modal-status-pill"
              style={{ background: statusColor.bg, color: statusColor.text, border: `1px solid ${statusColor.border}` }}
            >
              {form.status}
            </span>
          </div>
          <div className="modal-header-actions">
            {!editing ? (
              <>
                <button className="modal-btn-edit" onClick={() => setEditing(true)}>
                  <Pencil size={13} /> Edit
                </button>
                <button className="modal-btn-delete" onClick={() => setShowDeleteConfirm(true)}>
                  <Trash2 size={13} />
                </button>
              </>
            ) : (
              <>
                <button className="modal-btn-cancel" onClick={() => { setForm({ ...job }); setEditing(false); }}>
                  Cancel
                </button>
                <button className="modal-btn-save" onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving...' : '✓ Save'}
                </button>
              </>
            )}
            <button className="modal-btn-close" onClick={onClose}>✕</button>
          </div>
        </div>

        {/* Body */}
        <div className="modal-body">
          <div className="modal-grid">
            {/* Left column */}
            <div className="modal-col">
              <div className="modal-section">
                <h3 className="modal-section-title">Application Info</h3>

                <Field label="Company">
                  {editing
                    ? <input className="modal-input" value={form.company} onChange={e => set('company', e.target.value)} />
                    : <span className="modal-value">{form.company}</span>}
                </Field>

                <Field label="Role / Position">
                  {editing
                    ? <input className="modal-input" value={form.role} onChange={e => set('role', e.target.value)} />
                    : <span className="modal-value">{form.role}</span>}
                </Field>

                <Field label="Source">
                  {editing
                    ? (
                      <select className="modal-select" value={form.source} onChange={e => set('source', e.target.value)}>
                        <option value="">Not specified</option>
                        {JOB_SOURCES.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    )
                    : <span className="modal-value">{form.source || '—'}</span>}
                </Field>

                <Field label="Job Posting Link">
                  {editing
                    ? <input className="modal-input" value={form.postingLink} onChange={e => set('postingLink', e.target.value)} placeholder="https://..." />
                    : form.postingLink
                      ? <a href={form.postingLink} target="_blank" rel="noreferrer" className="modal-link">View Posting ↗</a>
                      : <span className="modal-value">—</span>}
                </Field>

                <Field label="Application Date">
                  {editing
                    ? <input className="modal-input" type="date" value={form.applicationDate ? form.applicationDate.split('T')[0] : ''} onChange={e => set('applicationDate', e.target.value)} />
                    : <span className="modal-value">{fmtDate(form.applicationDate)}</span>}
                </Field>

                <Field label="Last Update">
                  <span className="modal-value">{fmtDate(form.lastUpdateDate)}</span>
                </Field>
              </div>

              <div className="modal-section">
                <h3 className="modal-section-title">Location & Compensation</h3>

                <Field label="Location">
                  {editing
                    ? <input className="modal-input" value={form.location} onChange={e => set('location', e.target.value)} placeholder="e.g. Makati, Metro Manila" />
                    : <span className="modal-value">{form.location || '—'}</span>}
                </Field>

                <Field label="Work Setup">
                  {editing
                    ? (
                      <select className="modal-select" value={form.workSetup} onChange={e => set('workSetup', e.target.value)}>
                        {WORK_SETUPS.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    )
                    : <span className="modal-value">{form.workSetup}</span>}
                </Field>

                {editing ? (
                  <>
                    <div className="salary-mode-row">
                      <span className="salary-mode-label">Salary</span>
                      <div className="salary-mode-toggle">
                        <button type="button" className={`stoggle-btn ${salaryMode === 'range' ? 'active' : ''}`}
                          onClick={() => setSalaryMode('range')}>Min / Max</button>
                        <button type="button" className={`stoggle-btn ${salaryMode === 'exact' ? 'active' : ''}`}
                          onClick={() => { setSalaryMode('exact'); set('salaryMax', form.salaryMin || null); }}>Exact</button>
                      </div>
                    </div>
                    {salaryMode === 'range' ? (
                      <div className="modal-row-2">
                        <Field label="Salary Min">
                          <input className="modal-input" type="number" value={form.salaryMin || ''} onChange={e => set('salaryMin', e.target.value ? Number(e.target.value) : null)} placeholder="e.g. 40000" />
                        </Field>
                        <Field label="Salary Max">
                          <input className="modal-input" type="number" value={form.salaryMax || ''} onChange={e => set('salaryMax', e.target.value ? Number(e.target.value) : null)} placeholder="e.g. 60000" />
                        </Field>
                      </div>
                    ) : (
                      <Field label="Salary (Exact)">
                        <input className="modal-input" type="number" value={form.salaryMin || ''}
                          onChange={e => { const v = e.target.value ? Number(e.target.value) : null; set('salaryMin', v); set('salaryMax', v); }}
                          placeholder="e.g. 50000" />
                      </Field>
                    )}
                    <Field label="Currency">
                      <input className="modal-input" value={form.salaryCurrency} onChange={e => set('salaryCurrency', e.target.value)} placeholder="PHP" />
                    </Field>
                  </>
                ) : (
                  <Field label="Salary">
                    <span className="modal-value">{fmtSalaryView(form)}</span>
                  </Field>
                )}
              </div>
            </div>

            {/* Right column */}
            <div className="modal-col">
              <div className="modal-section">
                <h3 className="modal-section-title">Application Status</h3>

                <Field label="Status">
                  {editing
                    ? (
                      <select className="modal-select" value={form.status} onChange={e => set('status', e.target.value)}>
                        {JOB_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    )
                    : (
                      <span
                        className="status-pill-lg"
                        style={{ background: statusColor.bg, color: statusColor.text, border: `1px solid ${statusColor.border}` }}
                      >
                        {form.status}
                      </span>
                    )}
                </Field>

                <Field label="Stage">
                  {editing
                    ? (
                      <select className="modal-select" value={form.stage} onChange={e => set('stage', e.target.value)}>
                        <option value="">—</option>
                        {JOB_STAGES.filter(s => s).map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    )
                    : <span className="modal-value">{form.stage || '—'}</span>}
                </Field>

                <Field label="Next Step / Action">
                  {editing
                    ? <input className="modal-input" value={form.nextStep} onChange={e => set('nextStep', e.target.value)} placeholder="e.g. Prepare for technical interview on May 5" />
                    : <span className="modal-value">{form.nextStep || '—'}</span>}
                </Field>
              </div>

              <div className="modal-section">
                <h3 className="modal-section-title">Benefits & Notes</h3>

                <Field label="Benefits">
                  {editing
                    ? <textarea className="modal-textarea" value={form.benefits} onChange={e => set('benefits', e.target.value)} placeholder="e.g. HMO, 14th month pay, hybrid setup, meal allowance" rows={3} />
                    : <span className="modal-value modal-pre">{form.benefits || '—'}</span>}
                </Field>

                <Field label="Notes">
                  {editing
                    ? <textarea className="modal-textarea" value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Any additional notes about this application..." rows={4} />
                    : <span className="modal-value modal-pre">{form.notes || '—'}</span>}
                </Field>
              </div>
            </div>
          </div>
        </div>

        {/* Delete confirm */}
        {showDeleteConfirm && (
          <div className="modal-confirm-overlay">
            <div className="modal-confirm">
              <h3>Delete Application?</h3>
              <p>Are you sure you want to remove <strong>{job.company} – {job.role}</strong>? This cannot be undone.</p>
              <div className="modal-confirm-actions">
                <button className="modal-btn-cancel" onClick={() => setShowDeleteConfirm(false)}>Cancel</button>
                <button className="modal-btn-delete-confirm" onClick={handleDelete}>Yes, Delete</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default JobModal;
